/**
 * PriceChart — Interactive price chart using Lightweight Charts (TradingView).
 *
 * Owns its own data fetching via usePriceHistory hook.
 * Supports candlestick and line/area chart modes with crosshair tooltip.
 *
 * Story 4.3 — Task 2
 */

import type { PriceCandle, PriceInterval } from "@repo/shared-types";
import {
	AreaSeries,
	CandlestickSeries,
	ColorType,
	CrosshairMode,
	type IChartApi,
	type ISeriesApi,
	type MouseEventParams,
	type Time,
	type UTCTimestamp,
	isUTCTimestamp,
} from "lightweight-charts";
import { useCallback, useEffect, useRef, useState } from "react";

import { usePriceHistory } from "../../hooks/api/use-price-history";
import { formatCompact, formatPrice } from "../../lib/format-number";
import { type ChartType, ChartTypeToggle } from "./ChartTypeToggle";
import { TimeRangeSelector } from "./TimeRangeSelector";

type PriceChartProps = {
	tokenId: string;
};

type OhlcvTooltip = {
	time: string;
	open: number;
	high: number;
	low: number;
	close: number;
	volume: number;
} | null;

function formatChartTime(timestamp: number, interval: PriceInterval): string {
	const date = new Date(timestamp * 1000);
	if (interval === "1H" || interval === "4H") {
		return date.toLocaleString(undefined, {
			month: "short",
			day: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		});
	}
	return date.toLocaleDateString(undefined, {
		month: "short",
		day: "numeric",
		year: "numeric",
	});
}

function toUTCTimestamp(seconds: number): UTCTimestamp {
	// Branded type factory — same pattern as branded ID factories in shared-types.
	// UTCTimestamp is Nominal<number, "UTCTimestamp"> from lightweight-charts.
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- branded factory
	return seconds as UTCTimestamp;
}

function findCandleForTime(candles: PriceCandle[], time: Time): PriceCandle | undefined {
	if (!isUTCTimestamp(time)) return undefined;
	return candles.find((c) => c.timestamp === time);
}

export function PriceChart({ tokenId }: PriceChartProps) {
	const [selectedInterval, setSelectedInterval] = useState<PriceInterval>("1D");
	const [chartType, setChartType] = useState<ChartType>("candlestick");
	const [tooltip, setTooltip] = useState<OhlcvTooltip>(null);

	const [chartReady, setChartReady] = useState(false);

	const containerRef = useRef<HTMLDivElement>(null);
	const chartRef = useRef<IChartApi | null>(null);
	const candlestickSeriesRef = useRef<ISeriesApi<"Candlestick"> | null>(null);
	const areaSeriesRef = useRef<ISeriesApi<"Area"> | null>(null);

	const { data, isLoading, error, refetch } = usePriceHistory(tokenId, selectedInterval);

	const candles = data?.data ?? [];
	const dataSource = data?.meta?.dataSource ?? "indexer";

	// Build crosshair handler
	const handleCrosshairMove = useCallback(
		(param: MouseEventParams<Time>) => {
			if (!param.time || !param.point || candles.length === 0) {
				setTooltip(null);
				return;
			}

			const candle = findCandleForTime(candles, param.time);
			if (!candle) {
				setTooltip(null);
				return;
			}

			setTooltip({
				time: formatChartTime(candle.timestamp, selectedInterval),
				open: candle.open,
				high: candle.high,
				low: candle.low,
				close: candle.close,
				volume: candle.volume,
			});
		},
		[candles, selectedInterval]
	);

	// Create chart on mount, remove on unmount
	useEffect(() => {
		const container = containerRef.current;
		if (!container) return;

		// Dynamic import to avoid SSR issues
		let cancelled = false;

		async function initChart() {
			const { createChart } = await import("lightweight-charts");
			if (cancelled || !container) return;

			const chart = createChart(container, {
				autoSize: true,
				layout: {
					background: { type: ColorType.Solid, color: "transparent" },
					textColor: "#6b7280",
					fontFamily: "inherit",
				},
				grid: {
					vertLines: { color: "rgba(0,0,0,0.04)" },
					horzLines: { color: "rgba(0,0,0,0.04)" },
				},
				crosshair: {
					mode: CrosshairMode.Normal,
				},
				rightPriceScale: {
					borderColor: "rgba(0,0,0,0.08)",
				},
				timeScale: {
					borderColor: "rgba(0,0,0,0.08)",
					timeVisible: true,
					secondsVisible: false,
				},
				handleScroll: {
					mouseWheel: true,
					pressedMouseMove: true,
					horzTouchDrag: true,
					vertTouchDrag: false,
				},
				handleScale: {
					axisPressedMouseMove: true,
					mouseWheel: true,
					pinch: true,
				},
			});

			chartRef.current = chart;

			// Add both series up front, toggle visibility
			const cSeries = chart.addSeries(CandlestickSeries, {
				upColor: "#22c55e",
				downColor: "#ef4444",
				borderUpColor: "#22c55e",
				borderDownColor: "#ef4444",
				wickUpColor: "#22c55e",
				wickDownColor: "#ef4444",
			});
			candlestickSeriesRef.current = cSeries;

			const aSeries = chart.addSeries(AreaSeries, {
				lineColor: "#6366f1",
				topColor: "rgba(99,102,241,0.3)",
				bottomColor: "rgba(99,102,241,0.02)",
				lineWidth: 2,
			});
			areaSeriesRef.current = aSeries;

			if (!cancelled) setChartReady(true);
		}

		void initChart();

		return () => {
			cancelled = true;
			if (chartRef.current) {
				chartRef.current.remove();
				chartRef.current = null;
				candlestickSeriesRef.current = null;
				areaSeriesRef.current = null;
			}
			setChartReady(false);
		};
	}, []);

	// Update data when candles change or chart becomes ready
	useEffect(() => {
		const cSeries = candlestickSeriesRef.current;
		const aSeries = areaSeriesRef.current;
		if (!cSeries || !aSeries || !chartReady) return;

		if (candles.length === 0) {
			cSeries.setData([]);
			aSeries.setData([]);
			return;
		}

		const candlestickData = candles.map((c) => ({
			time: toUTCTimestamp(c.timestamp),
			open: c.open,
			high: c.high,
			low: c.low,
			close: c.close,
		}));

		const areaData = candles.map((c) => ({
			time: toUTCTimestamp(c.timestamp),
			value: c.close,
		}));

		cSeries.setData(candlestickData);
		aSeries.setData(areaData);

		// Fit content after data update
		chartRef.current?.timeScale().fitContent();
	}, [candles, chartReady]);

	// Toggle series visibility based on chart type
	useEffect(() => {
		const cSeries = candlestickSeriesRef.current;
		const aSeries = areaSeriesRef.current;
		if (!cSeries || !aSeries || !chartReady) return;

		if (chartType === "candlestick") {
			cSeries.applyOptions({ visible: true });
			aSeries.applyOptions({ visible: false });
		} else {
			cSeries.applyOptions({ visible: false });
			aSeries.applyOptions({ visible: true });
		}
	}, [chartType, chartReady]);

	// Subscribe/unsubscribe crosshair
	useEffect(() => {
		const chart = chartRef.current;
		if (!chart || !chartReady) return;

		chart.subscribeCrosshairMove(handleCrosshairMove);
		return () => {
			chart.unsubscribeCrosshairMove(handleCrosshairMove);
		};
	}, [handleCrosshairMove, chartReady]);

	const showChart = !error && !isLoading && candles.length > 0;

	return (
		<div className="rounded-xl border border-pastel-border bg-white p-5">
			{/* Header */}
			<div className="flex items-center justify-between mb-4">
				<h3 className="text-sm font-semibold text-pastel-black">Price History</h3>
				{showChart && (
					<div className="flex items-center gap-2">
						<TimeRangeSelector selected={selectedInterval} onIntervalChange={setSelectedInterval} />
						<ChartTypeToggle selected={chartType} onChartTypeChange={setChartType} />
					</div>
				)}
			</div>

			{/* OHLCV Tooltip */}
			{showChart && tooltip && (
				<div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-pastel-gray mb-2">
					<span>{tooltip.time}</span>
					<span>O: {formatPrice(tooltip.open)}</span>
					<span>H: {formatPrice(tooltip.high)}</span>
					<span>L: {formatPrice(tooltip.low)}</span>
					<span>C: {formatPrice(tooltip.close)}</span>
					<span>V: {formatCompact(tooltip.volume)}</span>
				</div>
			)}

			{/* Chart area — container always in DOM so the chart initializes on mount */}
			<div className="relative h-[280px] md:h-[400px]">
				<div
					ref={containerRef}
					className="w-full h-full"
					data-testid="price-chart-container"
					style={showChart ? undefined : { visibility: "hidden" }}
				/>

				{/* Overlay states render on top of the chart container */}
				{error && (
					<div className="absolute inset-0 bg-white flex flex-col items-center justify-center">
						<p className="text-sm text-pastel-gray mb-3">Unable to load chart</p>
						<button
							type="button"
							onClick={() => void refetch()}
							className="px-4 py-2 text-sm font-medium text-white bg-pastel-vibrant-blue rounded-lg hover:opacity-90 transition-opacity"
						>
							Retry
						</button>
					</div>
				)}

				{!error && isLoading && (
					<div className="absolute inset-0 bg-white rounded-lg animate-pulse">
						<div className="h-full bg-pastel-light-gray rounded-lg" />
					</div>
				)}

				{!error && !isLoading && candles.length === 0 && (
					<div className="absolute inset-0 bg-white flex flex-col items-center justify-center">
						<svg
							className="h-12 w-12 text-pastel-light-gray mb-3"
							viewBox="0 0 24 24"
							fill="none"
							stroke="currentColor"
							strokeWidth="1.5"
							aria-hidden="true"
						>
							<polyline points="22,6 13,15 9,11 2,18" />
							<polyline points="16,6 22,6 22,12" />
						</svg>
						<p className="text-sm text-pastel-gray">No price data available yet</p>
					</div>
				)}
			</div>

			{/* Data source indicator */}
			{showChart && dataSource === "seeded" && (
				<div className="mt-2">
					<span className="inline-flex items-center rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-700">
						Sample Data
					</span>
				</div>
			)}
		</div>
	);
}

export function PriceChartSkeleton() {
	return (
		<div className="rounded-xl border border-pastel-border bg-white p-5 animate-pulse">
			<div className="flex items-center justify-between mb-4">
				<div className="h-4 w-28 bg-pastel-light-gray rounded" />
				<div className="flex gap-1">
					{Array.from({ length: 4 }).map((_, i) => (
						<div
							key={`tab-skeleton-${String(i)}`}
							className="h-7 w-10 bg-pastel-light-gray rounded-lg"
						/>
					))}
				</div>
			</div>
			<div className="h-[280px] md:h-[400px] bg-pastel-light-gray rounded-lg" />
		</div>
	);
}
